export type TailorEvidenceMode = "raw" | "compatible" | "corrected";
export type TailorTrustStatus = "trusted" | "flagged" | "rejected";

export interface TailorAxisInput {
  id: string;
  label: string;
  min: number | null;
  max: number | null;
  mean: number | null;
  held_default?: number | null;
  sample_count?: number;
}

export interface TailorTrustLimits {
  hiddenRmsLimit: number;
  hiddenMaxDeltaLimit: number;
  correctionAbsLimit: number;
  correctionRelativeLimit: number;
  compatibleRmsLimit: number;
  compatibleMaxDeltaLimit: number;
}

export interface TailorSliceModel {
  output: string;
  axes: TailorAxisInput[];
  coefficients: number[];
  rmse: number | null;
  trainCount: number;
  outputMean: number;
  outputScale: number;
}

export interface TailorCorrectionAudit {
  rawValue: number;
  modelAtRaw: number;
  modelAtSlice: number;
  correctedValue: number;
  correctionDelta: number;
  hiddenRms: number;
  hiddenMaxDelta: number;
  trustScore: number;
  status: TailorTrustStatus;
  compatible: boolean;
}

export interface TailorEvidenceSummary {
  raw: number;
  compatible: number;
  correctedTotal: number;
  correctedTrusted: number;
  correctedFlagged: number;
  correctedRejected: number;
  meanHiddenRms: number | null;
  maxHiddenJump: number | null;
  maxCorrectionDelta: number | null;
}

export interface TailorRunSuggestion {
  id: string;
  xAxis: string;
  yAxis: string;
  values: Record<string, number>;
  predicted: number;
  nearestDistance: number;
  score: number;
  reason: string;
}

export const DEFAULT_TAILOR_TRUST_LIMITS: TailorTrustLimits = {
  hiddenRmsLimit: 0.18,
  hiddenMaxDeltaLimit: 0.36,
  correctionAbsLimit: 0.18,
  correctionRelativeLimit: 0.38,
  compatibleRmsLimit: 0.075,
  compatibleMaxDeltaLimit: 0.18,
};

function numericValue(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function axisDefault(axis: TailorAxisInput): number {
  return numericValue(axis.held_default) ?? numericValue(axis.mean) ?? numericValue(axis.min) ?? 0;
}

function axisSpan(axis: TailorAxisInput): number {
  const min = numericValue(axis.min);
  const max = numericValue(axis.max);
  if (min !== null && max !== null && Math.abs(max - min) > 1e-12) return Math.abs(max - min);
  return 1;
}

function axisRaw(row: Record<string, unknown>, axis: TailorAxisInput): number {
  return numericValue(row[axis.id]) ?? axisDefault(axis);
}

function axisNorm(value: number, axis: TailorAxisInput): number {
  return (value - axisDefault(axis)) / axisSpan(axis);
}

function featureVector(row: Record<string, unknown>, axes: TailorAxisInput[]): number[] {
  const values = [1];
  for (const axis of axes) {
    const z = axisNorm(axisRaw(row, axis), axis);
    values.push(z);
  }
  for (const axis of axes) {
    const z = axisNorm(axisRaw(row, axis), axis);
    values.push(z * z);
  }
  return values;
}

function solveLinearSystem(matrix: number[][], vector: number[]): number[] | null {
  const n = vector.length;
  const a = matrix.map((row, index) => [...row, vector[index] ?? 0]);
  for (let col = 0; col < n; col += 1) {
    let pivot = col;
    for (let row = col + 1; row < n; row += 1) {
      if (Math.abs(a[row]![col]!) > Math.abs(a[pivot]![col]!)) pivot = row;
    }
    if (Math.abs(a[pivot]![col]!) < 1e-10) return null;
    if (pivot !== col) [a[pivot], a[col]] = [a[col]!, a[pivot]!];
    const denom = a[col]![col]!;
    for (let item = col; item <= n; item += 1) a[col]![item]! /= denom;
    for (let row = 0; row < n; row += 1) {
      if (row === col) continue;
      const factor = a[row]![col]!;
      if (Math.abs(factor) < 1e-14) continue;
      for (let item = col; item <= n; item += 1) {
        a[row]![item]! -= factor * a[col]![item]!;
      }
    }
  }
  return a.map((row) => row[n] ?? 0);
}

export function fitTailorSliceModel(
  rows: Record<string, unknown>[],
  axes: TailorAxisInput[],
  output: string,
  maxTrainRows = 1600,
): TailorSliceModel | null {
  const usableAxes = axes
    .filter((axis) => axis.sample_count == null || axis.sample_count >= 3)
    .filter((axis) => axisSpan(axis) > 1e-12)
    .slice(0, 18);
  const samples = rows.filter((row) => numericValue(row[output]) !== null);
  if (usableAxes.length < 1 || samples.length < Math.max(8, usableAxes.length + 3)) return null;
  const stride = Math.max(1, Math.ceil(samples.length / maxTrainRows));
  const training = samples.filter((_, index) => index % stride === 0).slice(0, maxTrainRows);
  const termCount = 1 + usableAxes.length * 2;
  const ata = Array.from({ length: termCount }, () => Array.from({ length: termCount }, () => 0));
  const atb = Array.from({ length: termCount }, () => 0);
  let ySum = 0;
  for (const row of training) {
    const y = numericValue(row[output])!;
    ySum += y;
    const features = featureVector(row, usableAxes);
    for (let i = 0; i < termCount; i += 1) {
      atb[i]! += features[i]! * y;
      for (let j = 0; j < termCount; j += 1) ata[i]![j]! += features[i]! * features[j]!;
    }
  }
  const ridge = Math.max(1e-6, training.length * 1e-4);
  for (let i = 1; i < termCount; i += 1) ata[i]![i]! += ridge;
  const coefficients = solveLinearSystem(ata, atb);
  if (!coefficients) return null;
  let err = 0;
  let count = 0;
  for (const row of training) {
    const y = numericValue(row[output]);
    const pred = predictTailorSliceModel({ axes: usableAxes, coefficients }, row);
    if (y === null || !Number.isFinite(pred)) continue;
    err += (pred - y) ** 2;
    count += 1;
  }
  const outputMean = ySum / training.length;
  const outputValues = training.map((row) => numericValue(row[output])).filter((value): value is number => value !== null);
  const outputMin = Math.min(...outputValues);
  const outputMax = Math.max(...outputValues);
  return {
    output,
    axes: usableAxes,
    coefficients,
    trainCount: training.length,
    rmse: count ? Math.sqrt(err / count) : null,
    outputMean,
    outputScale: Math.max(1e-9, outputMax - outputMin),
  };
}

export function predictTailorSliceModel(
  model: Pick<TailorSliceModel, "axes" | "coefficients">,
  row: Record<string, unknown>,
  overrides: Record<string, number> = {},
): number {
  const patched = overrides && Object.keys(overrides).length ? { ...row, ...overrides } : row;
  const features = featureVector(patched, model.axes);
  return features.reduce((sum, value, index) => sum + value * (model.coefficients[index] ?? 0), 0);
}

export function correctionForPair(
  row: Record<string, unknown>,
  model: TailorSliceModel | null,
  output: string,
  heldValues: Record<string, number>,
  xKey: string,
  yKey: string,
  trust: TailorTrustLimits = DEFAULT_TAILOR_TRUST_LIMITS,
): TailorCorrectionAudit | null {
  if (!model) return null;
  const rawValue = numericValue(row[output]);
  if (rawValue === null) return null;
  const hiddenAxes = model.axes.filter((axis) => axis.id !== xKey && axis.id !== yKey);
  let sum = 0;
  let maxDelta = 0;
  const overrides: Record<string, number> = {};
  for (const axis of hiddenAxes) {
    const held = numericValue(heldValues[axis.id]) ?? axisDefault(axis);
    const delta = Math.abs(axisNorm(axisRaw(row, axis), axis) - axisNorm(held, axis));
    sum += delta * delta;
    maxDelta = Math.max(maxDelta, delta);
    overrides[axis.id] = held;
  }
  const hiddenRms = hiddenAxes.length ? Math.sqrt(sum / hiddenAxes.length) : 0;
  const modelAtRaw = predictTailorSliceModel(model, row);
  const modelAtSlice = predictTailorSliceModel(model, row, overrides);
  const correctionDelta = modelAtSlice - modelAtRaw;
  const correctedValue = rawValue + correctionDelta;
  const correctionLimit = Math.max(trust.correctionAbsLimit, Math.abs(rawValue) * trust.correctionRelativeLimit);
  const trustScore = Math.max(
    hiddenRms / Math.max(1e-9, trust.hiddenRmsLimit),
    maxDelta / Math.max(1e-9, trust.hiddenMaxDeltaLimit),
    Math.abs(correctionDelta) / Math.max(1e-9, correctionLimit),
  );
  const status: TailorTrustStatus = trustScore <= 1 ? "trusted" : trustScore <= 1.75 ? "flagged" : "rejected";
  return {
    rawValue,
    modelAtRaw,
    modelAtSlice,
    correctedValue,
    correctionDelta,
    hiddenRms,
    hiddenMaxDelta: maxDelta,
    trustScore,
    status,
    compatible: hiddenRms <= trust.compatibleRmsLimit && maxDelta <= trust.compatibleMaxDeltaLimit,
  };
}

export function tailorValueForMode(
  mode: TailorEvidenceMode,
  row: Record<string, unknown>,
  model: TailorSliceModel | null,
  output: string,
  heldValues: Record<string, number>,
  xKey: string,
  yKey: string,
  trust: TailorTrustLimits = DEFAULT_TAILOR_TRUST_LIMITS,
): number | null {
  const raw = numericValue(row[output]);
  if (mode === "raw") return raw;
  const audit = correctionForPair(row, model, output, heldValues, xKey, yKey, trust);
  if (!audit) return null;
  if (mode === "compatible") return audit.compatible ? audit.rawValue : null;
  return audit.status === "rejected" ? null : audit.correctedValue;
}

export function summarizeTailorEvidence(
  rows: Record<string, unknown>[],
  model: TailorSliceModel | null,
  output: string,
  heldValues: Record<string, number>,
  xKey: string,
  yKey: string,
  trust: TailorTrustLimits = DEFAULT_TAILOR_TRUST_LIMITS,
): TailorEvidenceSummary {
  let raw = 0;
  let compatible = 0;
  let correctedTrusted = 0;
  let correctedFlagged = 0;
  let correctedRejected = 0;
  let rmsSum = 0;
  let auditCount = 0;
  let maxHiddenJump = 0;
  let maxCorrectionDelta = 0;
  for (const row of rows) {
    if (numericValue(row[output]) !== null) raw += 1;
    const audit = correctionForPair(row, model, output, heldValues, xKey, yKey, trust);
    if (!audit) continue;
    auditCount += 1;
    rmsSum += audit.hiddenRms;
    maxHiddenJump = Math.max(maxHiddenJump, audit.hiddenMaxDelta);
    maxCorrectionDelta = Math.max(maxCorrectionDelta, Math.abs(audit.correctionDelta));
    if (audit.compatible) compatible += 1;
    if (audit.status === "trusted") correctedTrusted += 1;
    else if (audit.status === "flagged") correctedFlagged += 1;
    else correctedRejected += 1;
  }
  return {
    raw,
    compatible,
    correctedTotal: correctedTrusted + correctedFlagged + correctedRejected,
    correctedTrusted,
    correctedFlagged,
    correctedRejected,
    meanHiddenRms: auditCount ? rmsSum / auditCount : null,
    maxHiddenJump: auditCount ? maxHiddenJump : null,
    maxCorrectionDelta: auditCount ? maxCorrectionDelta : null,
  };
}

function candidateValues(rows: Record<string, unknown>[], axis: TailorAxisInput): number[] {
  const values = rows
    .map((row) => numericValue(row[axis.id]))
    .filter((value): value is number => value !== null)
    .sort((a, b) => a - b);
  if (values.length < 6) {
    const min = numericValue(axis.min) ?? axisDefault(axis) - axisSpan(axis) / 2;
    const max = numericValue(axis.max) ?? axisDefault(axis) + axisSpan(axis) / 2;
    return [0.08, 0.24, 0.42, 0.58, 0.76, 0.92].map((q) => min + (max - min) * q);
  }
  return [0.08, 0.24, 0.42, 0.58, 0.76, 0.92].map((q) => values[Math.min(values.length - 1, Math.max(0, Math.floor(q * (values.length - 1))))]!);
}

export function recommendTailorRuns({
  rows,
  model,
  axes,
  heldValues,
  output,
  xKey,
  yKey,
  limit = 6,
}: {
  rows: Record<string, unknown>[];
  model: TailorSliceModel | null;
  axes: TailorAxisInput[];
  heldValues: Record<string, number>;
  output: string;
  xKey: string;
  yKey: string;
  limit?: number;
}): TailorRunSuggestion[] {
  if (!model) return [];
  const xAxis = axes.find((axis) => axis.id === xKey);
  const yAxis = axes.find((axis) => axis.id === yKey);
  if (!xAxis || !yAxis || xAxis.id === yAxis.id) return [];
  const xValues = candidateValues(rows, xAxis);
  const yValues = candidateValues(rows, yAxis);
  const observed = rows
    .map((row) => ({ x: numericValue(row[xKey]), y: numericValue(row[yKey]) }))
    .filter((point): point is { x: number; y: number } => point.x !== null && point.y !== null);
  const suggestions: TailorRunSuggestion[] = [];
  for (const x of xValues) {
    for (const y of yValues) {
      const values: Record<string, number> = {};
      for (const axis of axes) values[axis.id] = numericValue(heldValues[axis.id]) ?? axisDefault(axis);
      values[xKey] = x;
      values[yKey] = y;
      let nearest = Infinity;
      for (const point of observed) {
        const dx = (point.x - x) / axisSpan(xAxis);
        const dy = (point.y - y) / axisSpan(yAxis);
        nearest = Math.min(nearest, Math.sqrt(dx * dx + dy * dy));
      }
      const predicted = predictTailorSliceModel(model, values);
      const responseLift = Math.abs(predicted - model.outputMean) / Math.max(1e-9, model.outputScale);
      const score = nearest + responseLift * 0.28;
      const reason = nearest > 0.28
        ? `fills sparse ${xAxis.label}/${yAxis.label} slice evidence`
        : responseLift > 0.35
          ? `checks a high-response ${output} region`
          : `adds direct evidence at the held slice`;
      suggestions.push({
        id: `${xKey}:${x.toPrecision(5)}|${yKey}:${y.toPrecision(5)}`,
        xAxis: xKey,
        yAxis: yKey,
        values,
        predicted,
        nearestDistance: nearest,
        score,
        reason,
      });
    }
  }
  return suggestions
    .sort((left, right) => right.score - left.score)
    .filter((item, index, list) => list.findIndex((other) =>
      Math.abs(other.values[xKey]! - item.values[xKey]!) < axisSpan(xAxis) * 0.04
      && Math.abs(other.values[yKey]! - item.values[yKey]!) < axisSpan(yAxis) * 0.04
    ) === index)
    .slice(0, limit);
}
